/**
 * Task Detail Dialog
 * View and edit internal task details
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  CheckCircle as CompleteIcon,
  Replay as ReopenIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Description as WorkOrderIcon,
  Schedule as ScheduleIcon,
  PersonAdd as AssignIcon,
  PersonRemove as UnassignIcon,
  OpenInNew as OpenIcon,
  CloudUpload as UploadIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowRight as ExpandLessIcon,
  Visibility as ViewIcon,
  AutoAwesome as ExtractIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import TaskService from '../../services/task.service';
import ApiService from '../../services/api.service';
import { transactionNumbersOf, transactionNumberChoices } from '../../utils/transactionNumbers';
import TransactionNumberChoices from '../../components/work-orders/TransactionNumberChoices';

// Font sizes
const fontSize = {
  sm: '0.875rem',
  xs: '0.75rem',
  md: '1rem'
};

const TaskDetail = ({ open, taskId, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [task, setTask] = useState(null);
  const [editedNotes, setEditedNotes] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  // Any action taken here (assign, complete, reopen) that the tasks list must reflect on close.
  const [refreshOnClose, setRefreshOnClose] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [success, setSuccess] = useState(null);

  // Deliverable documents on the associated WO task — the assigned user can upload/email them here
  // without opening the work order. Reuses the WO deliverable endpoints.
  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}')?.id || null; } catch (e) { return null; }
  })();
  const [taskDocs, setTaskDocs] = useState([]);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docFiles, setDocFiles] = useState([]);
  // Deliverable doc type: 'APPLICATION_FORM' (default) or 'OTHER'; docTypeOther holds the optional
  // custom name used when 'OTHER' is selected.
  const [docType, setDocType] = useState('APPLICATION_FORM');
  const [docTypeOther, setDocTypeOther] = useState('');
  const [docTxn, setDocTxn] = useState('');
  // The service's current numbers; null until loaded, so an upload cannot replace numbers it has not seen.
  const [docExisting, setDocExisting] = useState(null);
  const [docUnticked, setDocUnticked] = useState([]);
  const [docUploading, setDocUploading] = useState(false);
  const [applicantDocs, setApplicantDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsExpanded, setDocsExpanded] = useState(false);
  const [viewingDocId, setViewingDocId] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [docExtracting, setDocExtracting] = useState(false);
  const [docError, setDocError] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailLoadingRecipients, setEmailLoadingRecipients] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState(null);

  const woId = task?.workOrderId || task?.work_order_id || null;
  const woTaskId = task?.workOrderTaskId || task?.work_order_task_id || null;
  const isAssignedToMe = Boolean(task && currentUserId
    && String(task.assignedTo ?? task.assigned_to ?? '') === String(currentUserId));
  const canManageDeliverables = Boolean(isAssignedToMe && woId && woTaskId);
  const docChoices = transactionNumberChoices(docExisting || [], docTxn, docUnticked);

  // Load task details
  useEffect(() => {
    if (open && taskId) {
      loadTask();
    }
  }, [open, taskId]);

  const fetchTaskDocs = async (wid, wtid, customerName) => {
    if (!wid || !wtid) { setTaskDocs([]); setApplicantDocs([]); return; }
    setDocsLoading(true);
    try {
      const resp = await ApiService.getWorkOrderDocuments(wid);
      const all = resp?.data?.data || [];
      const mine = all.filter(d => (d.work_order_task_id || d.workOrderTaskId) === wtid);
      setTaskDocs(mine);
      // customer_name is the only applicant link a document carries, as delegation also matches.
      const applicant = (customerName || '').trim().toLowerCase();
      const mineIds = new Set(mine.map(d => d.id));
      setApplicantDocs(applicant
        ? all.filter(d => !mineIds.has(d.id)
            && (d.customer_name || d.customerName || '').trim().toLowerCase() === applicant)
        : []);
    } catch (e) {
      setTaskDocs([]);
      setApplicantDocs([]);
    } finally {
      setDocsLoading(false);
    }
  };

  const totalDocCount = taskDocs.length + applicantDocs.length;

  const renderDocGroup = (heading, docs, emptyText) => (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
        {heading} ({docs.length})
      </Typography>
      {docs.length === 0 ? (
        <Typography sx={{ fontSize: fontSize.xs, color: 'text.disabled' }}>{emptyText}</Typography>
      ) : (
        docs.map((d) => (
          <Box
            key={d.id}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.5 }}
          >
            <Typography sx={{ fontSize: fontSize.sm, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} noWrap>
              {d.document_type || d.documentType || d.file_name || d.fileName || 'Document'}
            </Typography>
            <Tooltip title="Open document">
              <span>
                <IconButton size="small" disabled={viewingDocId === d.id} onClick={() => handleViewDoc(d)}>
                  {viewingDocId === d.id
                    ? <CircularProgress size={14} />
                    : <ViewIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))
      )}
    </Box>
  );

  const getFilePreviewType = (fileName) => {
    if (!fileName) return 'other';
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (['md', 'txt', 'json', 'xml', 'csv'].includes(ext)) return 'text';
    return 'other';
  };

  const closeDocPreview = () => {
    setPreviewDialogOpen(false);
    setPreviewUrl('');
    setPreviewDocument(null);
  };

  const handleViewDoc = async (doc) => {
    if (!woId || !doc?.id) return;
    setPreviewDocument(doc);
    setPreviewUrl('');
    setLoadingPreview(true);
    setPreviewDialogOpen(true);
    setViewingDocId(doc.id);
    try {
      const response = await ApiService.getWorkOrderDocumentDownloadUrl(woId, doc.id);
      const downloadUrl = response?.data?.data?.download_url || response?.data?.data?.downloadUrl;
      if (downloadUrl) {
        setPreviewUrl(downloadUrl);
      } else {
        setDocError('Failed to open the document.');
      }
    } catch (e) {
      setDocError('Failed to open the document.');
    } finally {
      setLoadingPreview(false);
      setViewingDocId(null);
    }
  };

  const loadTask = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await TaskService.getById(taskId);
      setTask(result);
      setEditedNotes(result.notes || '');
      // Load this task's deliverable documents (on the associated WO task).
      fetchTaskDocs(result.workOrderId || result.work_order_id,
        result.workOrderTaskId || result.work_order_task_id,
        result.customerName || result.customer_name);
      // Ensure selectedEmployee is always a string, never null/undefined
      // (MUI Select requires a controlled value throughout its lifetime)
      const assignee = result.assignedTo ?? result.assigned_to ?? '';
      setSelectedEmployee(assignee !== null ? String(assignee) : '');
      setHasChanges(false);

      // Fetch real TC users for the assignment dropdown
      const tcId = result.typingCenterId || result.typing_center_id;
      if (tcId) {
        fetchEmployees(tcId);
      }
    } catch (err) {
      console.error('Error loading task:', err);
      setError(err.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async (typingCenterId) => {
    setLoadingEmployees(true);
    try {
      const response = await ApiService.getTypingCenterUsers(typingCenterId);
      const users = response?.data?.data || response?.data || [];
      setEmployees(users);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Handle notes change
  const handleNotesChange = (e) => {
    setEditedNotes(e.target.value);
    setHasChanges(true);
  };

  // Handle employee selection
  const handleEmployeeChange = (e) => {
    setSelectedEmployee(e.target.value);
    setHasChanges(true);
  };

  // Save changes
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Update notes
      if (editedNotes !== (task.notes || '')) {
        await TaskService.update(task.id, { notes: editedNotes });
      }

      // Update assignment — always apply what the dropdown currently shows
      const empValue = selectedEmployee || '';
      if (empValue) {
        const employee = employees.find(e => String(e.id) === String(empValue));
        const name = employee?.fullName || employee?.full_name || employee?.name || 'Unknown';
        await TaskService.assign(task.id, empValue, name);
      } else {
        // Dropdown set to "Unassigned" — only unassign if currently assigned
        const currentAssignee = task.assignedTo || task.assigned_to;
        if (currentAssignee) {
          await TaskService.unassign(task.id);
        }
      }

      onClose(true); // Close and refresh the tasks list (assignment/notes changed)
    } catch (err) {
      console.error('Error saving task:', err);
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Mark complete
  const handleComplete = async () => {
    try {
      setSaving(true);
      setError(null);
      await TaskService.complete(task.id);
      setRefreshOnClose(true);
      await loadTask();
    } catch (err) {
      console.error('Error completing task:', err);
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to complete task');
    } finally {
      setSaving(false);
    }
  };

  // Reopen task
  const handleReopen = async () => {
    try {
      setSaving(true);
      setError(null);
      await TaskService.reopen(task.id);
      setRefreshOnClose(true);
      await loadTask();
    } catch (err) {
      console.error('Error reopening task:', err);
      setError(err.message || 'Failed to reopen task');
    } finally {
      setSaving(false);
    }
  };

  // Handle close
  const handleClose = () => {
    onClose(refreshOnClose || hasChanges);
  };

  // Navigate to work order
  const handleViewWorkOrder = () => {
    if (task?.workOrderId) {
      navigate(`/work-orders/${task.workOrderId}`);
      handleClose();
    }
  };

  // ---- Deliverable documents (upload + email), targeting the associated WO task ----
  const openDocDialog = () => {
    setDocFiles([]);
    setDocTxn('');
    setDocUnticked([]);
    setDocExisting(null);
    setDocError(null);
    setDocType('APPLICATION_FORM');
    setDocTypeOther('');
    setDocDialogOpen(true);
    ApiService.getWorkOrderById(woId)
      .then((resp) => {
        const data = resp?.data?.data || resp?.data;
        setDocExisting(transactionNumbersOf((data?.tasks || []).find((t) => t.id === woTaskId)));
      })
      .catch(() => setDocError('Could not load this service\'s transaction numbers. Close and reopen to try again.'));
  };

  // Try to auto-fill the transaction number by extracting the application number from the uploaded
  // document(s) via the TC's OpenAI key. Sequential (never parallel): stop on the first found number,
  // stop on the first error (e.g. missing/invalid key), and skip a document that yields nothing.
  const handleExtractAppNumber = async () => {
    if (!docFiles.length || !woId) return;
    setDocExtracting(true);
    setDocError(null);
    try {
      for (const file of docFiles) {
        let resp;
        try {
          resp = await ApiService.extractWorkOrderApplicationNumber(woId, file);
        } catch (e) {
          setDocError(e?.response?.data?.message || e?.response?.data?.error || 'Could not extract the application number.');
          return;
        }
        const appNo = resp?.data?.data?.application_number;
        if (appNo) {
          setDocTxn(appNo);
          return;
        }
      }
      setDocError('No application number found in the selected document(s).');
    } finally {
      setDocExtracting(false);
    }
  };

  const handleDocUpload = async () => {
    if (!docFiles.length || !woId || !woTaskId) return;
    setDocUploading(true);
    setDocError(null);
    // Deliverable doc type: Application Form by default, or a custom name (falling back to "other")
    // when Other is chosen.
    const typeLabel = docType === 'OTHER'
      ? (docTypeOther.trim() || 'other')
      : 'Application Form';
    const txn = transactionNumberChoices(docExisting || [], docTxn, docUnticked).value;
    const custName = task.customerName || task.customer_name || null;
    let failed = 0;
    try {
      for (const file of docFiles) {
        try {
          await ApiService.uploadWorkOrderDocument(woId, file, '', typeLabel, custName, woTaskId, txn);
        } catch (e) {
          if (txn && e?.response?.status === 400) {
            setDocError(e?.response?.data?.error || e?.response?.data?.message || 'This transaction number was refused.');
            return;
          }
          failed++;
        }
      }
      if (failed > 0) {
        setDocError(`${failed} of ${docFiles.length} file(s) failed to upload.`);
      } else {
        setDocDialogOpen(false);
        setSuccess('Documents uploaded');
      }
      await fetchTaskDocs(woId, woTaskId, task?.customerName || task?.customer_name);
    } finally {
      setDocUploading(false);
    }
  };

  const parseEmails = (s) => (s || '').split(/[,;\s]+/).map(e => e.trim()).filter(e => e.includes('@'));

  const openEmailDialog = async () => {
    setEmailRecipients('');
    setEmailCc('');
    setEmailError(null);
    setEmailDialogOpen(true);
    if (!woId) return;
    setEmailLoadingRecipients(true);
    try {
      const resp = await ApiService.getWorkOrderSuggestedRecipients(woId);
      setEmailRecipients((resp?.data?.data || []).join(', '));
    } catch (e) {
      // leave blank
    } finally {
      setEmailLoadingRecipients(false);
    }
  };

  const handleSendEmail = async () => {
    const recipients = parseEmails(emailRecipients);
    if (!recipients.length) {
      setEmailError('Enter at least one recipient email.');
      return;
    }
    setEmailSending(true);
    setEmailError(null);
    try {
      await ApiService.sendWorkOrderTaskEmail(woId, woTaskId, recipients, parseEmails(emailCc));
      setEmailDialogOpen(false);
      setSuccess('Email sent');
    } catch (e) {
      setEmailError(e.response?.data?.error || e.response?.data?.message || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TaskIcon sx={{ fontSize: 24, color: 'secondary.main' }} />
          <Box>
            <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, color: 'secondary.main' }}>
              {task?.taskNumber || 'Task Details'}
            </Typography>
            {task && (
              <Chip
                label={TaskService.getStatusLabel(task.status)}
                color={TaskService.getStatusColor(task.status)}
                size="small"
                sx={{ fontSize: fontSize.xs, height: 22, mt: 0.5 }}
              />
            )}
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={32} />
            <Typography sx={{ ml: 2, fontSize: fontSize.sm, color: 'text.secondary' }}>
              Loading task...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : task ? (
          <Box>
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>
            )}
            {/* Task Name */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 0.5 }}>
                Task Name
              </Typography>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                {task.taskName}
              </Typography>
            </Box>

            {/* Description */}
            {task.description && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 0.5 }}>
                  Description
                </Typography>
                <Typography sx={{ fontSize: fontSize.sm }}>
                  {task.description}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Work Order & Customer */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <WorkOrderIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                    Work Order
                  </Typography>
                </Box>
                {task.workOrderNumber ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: fontSize.sm, color: 'secondary.main', fontWeight: 500 }}>
                      {task.workOrderNumber}
                    </Typography>
                    {task.workOrderId && (
                      <Tooltip title="View Work Order">
                        <IconButton size="small" onClick={handleViewWorkOrder}>
                          <OpenIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.disabled' }}>
                    No work order
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                    Customer
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: fontSize.sm }}>
                  {task.customerName || '-'}
                </Typography>
              </Grid>
            </Grid>

            {/* Dates */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                    Created
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: fontSize.sm }}>
                  {TaskService.formatDateTime(task.createdAt)}
                </Typography>
              </Grid>
              {task.completedAt && (
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CompleteIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                      Completed
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: fontSize.sm }}>
                    {TaskService.formatDateTime(task.completedAt)}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Assignment */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                  Assignment
                </Typography>
              </Box>
              <FormControl fullWidth size="small" disabled={task.status === 'COMPLETED' || loadingEmployees}>
                <InputLabel sx={{ fontSize: fontSize.sm }}>Assigned To</InputLabel>
                <Select
                  value={selectedEmployee || ''}
                  label="Assigned To"
                  onChange={(e) => {
                    setSelectedEmployee(e.target.value || '');
                  }}
                  sx={{ fontSize: fontSize.sm }}
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id} sx={{ fontSize: fontSize.sm }}>
                      {emp.fullName || emp.full_name || emp.name} {emp.role ? `(${emp.role})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Notes */}
            <Box>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 1 }}>
                Notes
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                value={editedNotes}
                onChange={handleNotesChange}
                placeholder="Add notes about this task..."
                size="small"
                disabled={task.status === 'COMPLETED'}
                inputProps={{ sx: { fontSize: fontSize.sm } }}
              />
            </Box>

            {/* Deliverable documents — only the assigned user, uploads/emails on the WO task */}
            {canManageDeliverables && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Box
                    onClick={() => setDocsExpanded((v) => !v)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer', userSelect: 'none' }}
                  >
                    {docsExpanded
                      ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                    <UploadIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                      Documents {totalDocCount > 0 ? `(${totalDocCount})` : ''}
                    </Typography>
                  </Box>

                  <Collapse in={docsExpanded}>
                    <Box sx={{ mb: 1.5 }}>
                      {docsLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                          <CircularProgress size={14} />
                          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                            Loading documents...
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          {renderDocGroup('This task', taskDocs, 'No documents attached to this task yet.')}
                          {renderDocGroup(
                            task.customerName || task.customer_name
                              ? `Applicant — ${task.customerName || task.customer_name}`
                              : 'Applicant',
                            applicantDocs,
                            'No other documents for this applicant on the work order.'
                          )}
                        </>
                      )}
                    </Box>
                  </Collapse>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<UploadIcon />}
                      onClick={openDocDialog}
                      sx={{ fontSize: fontSize.sm }}
                    >
                      Attach documents
                    </Button>
                    <Tooltip title={taskDocs.length === 0 ? 'Attach documents first to email them' : ''}>
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EmailIcon />}
                          onClick={openEmailDialog}
                          disabled={taskDocs.length === 0}
                          sx={{ fontSize: fontSize.sm }}
                        >
                          Email documents
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {task && task.status === 'COMPLETED' ? (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<ReopenIcon />}
            onClick={handleReopen}
            disabled={saving}
            sx={{ fontSize: fontSize.sm }}
          >
            Reopen
          </Button>
        ) : task && (
          <Tooltip title={!(task.assignedTo || task.assigned_to) ? 'Assign this task to someone before completing' : ''}>
            <span>
              <Button
                variant="outlined"
                color="success"
                startIcon={<CompleteIcon />}
                onClick={handleComplete}
                disabled={saving || !(task.assignedTo || task.assigned_to)}
                sx={{ fontSize: fontSize.sm }}
              >
                Mark Complete
              </Button>
            </span>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleClose}
          sx={{ fontSize: fontSize.sm }}
        >
          Close
        </Button>
        {task && task.status !== 'COMPLETED' && (
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ fontSize: fontSize.sm }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* Attach deliverable documents to the associated WO task */}
    <Dialog open={docDialogOpen} onClose={docUploading ? undefined : () => setDocDialogOpen(false)} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 1.5 }}>
        <Box sx={{ flexShrink: 0, width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', color: 'success.main' }}>
          <UploadIcon fontSize="small" />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: fontSize.md }}>Upload deliverables</Typography>
          {task && (
            <Typography variant="body2" color="text.secondary" noWrap>{task.taskName || task.task_name}</Typography>
          )}
        </Box>
        <IconButton onClick={() => setDocDialogOpen(false)} size="small" disabled={docUploading} sx={{ mt: -0.5, mr: -0.5 }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 0.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {docError && <Alert severity="error" onClose={() => setDocError(null)}>{docError}</Alert>}

          <Box
            component="label"
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 0.5, py: 2.5, px: 2, textAlign: 'center', cursor: docUploading ? 'default' : 'pointer',
              border: '1.5px dashed', borderColor: 'divider', borderRadius: 2, color: 'text.secondary',
              transition: 'border-color .15s, background-color .15s',
              '&:hover': docUploading ? {} : { borderColor: 'success.main', bgcolor: 'action.hover' },
            }}
          >
            <UploadIcon sx={{ color: 'success.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {docFiles.length ? 'Add more files' : 'Drop files here or browse'}
            </Typography>
            <Typography variant="caption" color="text.secondary">PDF, JPG or PNG</Typography>
            <input type="file" hidden multiple disabled={docUploading}
              onChange={(e) => setDocFiles((prev) => [...prev, ...Array.from(e.target.files || [])])} />
          </Box>

          {docFiles.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {docFiles.map((f, idx) => (
                <Box key={`${f.name}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>{f.name}</Typography>
                  <IconButton size="small" disabled={docUploading} onClick={() => setDocFiles((prev) => prev.filter((_, i) => i !== idx))} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.75, display: 'block' }}>Document type</Typography>
            <ToggleButtonGroup value={docType} exclusive size="small" fullWidth disabled={docUploading}
              onChange={(e, v) => v && setDocType(v)}>
              <ToggleButton value="APPLICATION_FORM" sx={{ textTransform: 'none' }}>Application Form</ToggleButton>
              <ToggleButton value="OTHER" sx={{ textTransform: 'none' }}>Other</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {docType === 'OTHER' && (
            <TextField fullWidth size="small" label="Document type name" placeholder={'Leave blank to save as "other"'}
              value={docTypeOther} onChange={(e) => setDocTypeOther(e.target.value)} disabled={docUploading} />
          )}

          <TextField
            fullWidth size="small" label="Transaction number"
            value={docTxn} onChange={(e) => setDocTxn(e.target.value)} disabled={docUploading}
            helperText="Optional — or pull it from the document"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleExtractAppNumber}
                    disabled={!docFiles.length || docExtracting || docUploading}
                    startIcon={docExtracting ? <CircularProgress size={14} color="inherit" /> : <ExtractIcon sx={{ fontSize: 16, color: '#D4A017' }} />}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                    {docExtracting ? 'Reading…' : 'Extract'}
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <TransactionNumberChoices
            existing={docExisting || []}
            choices={docChoices}
            unticked={docUnticked}
            disabled={docUploading}
            onToggle={(key) => setDocUnticked((prev) => (
              prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
            ))}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button
          variant="contained"
          onClick={handleDocUpload}
          disabled={!docFiles.length || docUploading || docExisting === null || docChoices.tooMany}
          startIcon={docUploading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: 'none', minWidth: 120, borderRadius: 2 }}
        >
          {docUploading ? 'Uploading…' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Email the associated WO task's documents to the client */}
    <Dialog open={emailDialogOpen} onClose={emailSending ? undefined : () => setEmailDialogOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: fontSize.md, fontWeight: 600 }}>Email documents</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {emailError && <Alert severity="error" onClose={() => setEmailError(null)}>{emailError}</Alert>}
          <TextField
            fullWidth
            size="small"
            label="To"
            placeholder="name@example.com, another@example.com"
            value={emailRecipients}
            onChange={(e) => setEmailRecipients(e.target.value)}
            disabled={emailSending || emailLoadingRecipients}
            helperText={emailLoadingRecipients ? 'Loading suggested recipients…' : 'Separate multiple addresses with commas'}
          />
          <TextField
            fullWidth
            size="small"
            label="Cc (optional)"
            value={emailCc}
            onChange={(e) => setEmailCc(e.target.value)}
            disabled={emailSending}
          />
          <Typography variant="caption" color="text.secondary">
            The task's attached documents are sent using the Work Order Completed email template.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setEmailDialogOpen(false)} disabled={emailSending} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSendEmail}
          disabled={emailSending || emailLoadingRecipients}
          startIcon={emailSending ? <CircularProgress size={16} color="inherit" /> : <EmailIcon fontSize="small" />}
        >
          {emailSending ? 'Sending…' : 'Send email'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={previewDialogOpen} onClose={closeDocPreview} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 600, pr: 2, wordBreak: 'break-all' }}>
          {previewDocument?.file_name || previewDocument?.fileName || 'Document'}
        </Typography>
        <IconButton size="small" onClick={closeDocPreview}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 320 }}>
        {loadingPreview ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : !previewUrl ? (
          <Typography color="text.secondary" textAlign="center" py={6}>Could not load the document.</Typography>
        ) : getFilePreviewType(previewDocument?.file_name || previewDocument?.fileName) === 'image' ? (
          <Box textAlign="center">
            <img
              src={previewUrl}
              alt={previewDocument?.file_name || previewDocument?.fileName || 'document'}
              style={{ maxWidth: '100%', maxHeight: '72vh', objectFit: 'contain' }}
            />
          </Box>
        ) : getFilePreviewType(previewDocument?.file_name || previewDocument?.fileName) === 'pdf' ? (
          <Box component="iframe" title="document-preview" src={previewUrl}
            sx={{ width: '100%', height: '72vh', border: 'none' }} />
        ) : (
          <Box textAlign="center" py={5}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              This file type can't be previewed here.
            </Typography>
            <Button variant="outlined" onClick={() => window.open(previewUrl, '_blank', 'noopener')} sx={{ textTransform: 'none' }}>
              Open in new tab
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => window.open(previewUrl, '_blank', 'noopener')} disabled={!previewUrl} sx={{ textTransform: 'none' }}>
          Open in new tab
        </Button>
        <Button onClick={closeDocPreview} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default TaskDetail;
